import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  Alert,
  Box,
  Stack,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Snackbar,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { X } from "@phosphor-icons/react";

import { ANLoadingScreen } from "@answersnow/pkg-theme";
import { previousPaper, space5 } from "./styled";
import AssessmentCard from "./components/AssessmentCard";
import AssessmentResults from "./components/AssessmentResults";
import DataEntryDialogContent from "./components/DataEntryDialogContent";
import SendQabfAssessmentDialog from "./components/SendQabfAssessmentDialog";
import {
  ASSESSMENT_DEFINITIONS,
  getAssessmentRecordsByType,
  getAssessmentDefinition,
} from "./assessmentConfig";
import { useAssessmentsData } from "./hooks/useAssessmentsData";
import { useCreateUserTask } from "hooks/api/user/useCreateUserTask";
import { useGetUserTask } from "hooks/api/user/useGetUserTask";
import * as selectors from "selectors";
import { getUser, getUserId } from "selectors";
import { getAge } from "utils/getAge";
import type { AssessmentRecord, AssessmentType } from "./types";

const PANEL_WIDTH = 512;
// Width of the left-hand SideNav drawer (from SideNavDrawer.js).
const SIDENAV_WIDTH = 256;
// Main content takes more space when panel is closed
const MAIN_CONTENT_FLEX_GROW = 4;

type AssessmentOverlayMode = "results" | "addAssessment";

function getDataEntryDialogTitle(assessmentType: AssessmentType): string {
  switch (assessmentType) {
    case "vbmappMilestones":
      return "Add VB-MAPP Milestone assessment";
    case "afls":
      return "Add AFLS assessment";
    case "srs2":
      return "Add SRS-2 assessment";
    case "peak":
      return "Add PEAK assessment";
    default:
      return "Add assessment";
  }
}

/**
 * The Assessments component renders client assessment records. Assessment results open in a
 * persistent right-hand Drawer; adding a new assessment opens a centered Dialog.
 * @component
 * @returns {JSX.Element} - The `Assessments` component
 */
const Assessments = () => {
  const { id: routeClientId } = useParams<{ id: string }>();
  const customerClientId = useSelector(selectors.getCustomerClientId);
  const clientId = customerClientId || routeClientId;
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState<AssessmentOverlayMode>("results");
  const [selectedAssessmentType, setSelectedAssessmentType] = useState<AssessmentType>("vineland");
  const [isQabfDialogOpen, setIsQabfDialogOpen] = useState(false);
  const [cfql2SendResult, setCfql2SendResult] = useState<"success" | "error" | null>(null);

  const customerDetails = useSelector(selectors.getCustomerDetails);
  const clientUserId = useSelector(selectors.getCustomerUserId);
  const currentUserName: string = useSelector(getUser)?.name ?? "";
  const clinicianUserId: number | null = useSelector(getUserId) ?? null;
  const clientDob: string | null = customerDetails?.details?.dob ?? null;
  const clientAgeInYears: number | null = clientDob ? getAge(clientDob) : null;
  const clientGuid: string | null = customerDetails?.details?.video_call_guid ?? null;

  const { clientAssessments, assessmentsLoading, cardStatusByType } = useAssessmentsData(
    clientId,
    clientAgeInYears
  );

  const handleOpenResultsPanel = useCallback((assessmentType: AssessmentType) => {
    setSelectedAssessmentType(assessmentType);
    setOverlayMode("results");
    setIsOverlayOpen(true);
  }, []);

  const handleOpenAddAssessmentDialog = useCallback(
    (assessmentType: AssessmentType) => {
      if (!clientId) {
        return;
      }
      setSelectedAssessmentType(assessmentType);
      setOverlayMode("addAssessment");
      setIsOverlayOpen(true);
    },
    [clientId]
  );

  const handleCloseOverlay = useCallback(() => {
    setIsOverlayOpen(false);
  }, []);

  const handleOpenQabfDialog = useCallback(() => {
    setIsQabfDialogOpen(true);
  }, []);

  const handleCloseQabfDialog = useCallback(() => {
    setIsQabfDialogOpen(false);
  }, []);

  const { createTaskAsync, isLoading: isCreatingCfqlTask } = useCreateUserTask();

  // A caregiver may only have one pending CFQL task at a time. Fetch any
  // pending (incomplete) CFQL task so we can disable the "Send Assessment"
  // button and avoid stacking duplicates (ANOW-6899). The create mutation
  // invalidates the GET_USER_TASKS key on success, so this refetches
  // automatically after a send.
  const pendingCfqlParams = useMemo(
    () =>
      clientId
        ? {
            client_id: Number(clientId),
            type: "fillout_iframe",
            completed: "false",
            title: "CFQL",
          }
        : null,
    [clientId]
  );
  const { tasks: pendingCfqlTasks } = useGetUserTask(clientUserId as number, pendingCfqlParams);
  const hasPendingCfqlTask = (pendingCfqlTasks?.length ?? 0) > 0;
  const isSendCfqlDisabled = hasPendingCfqlTask || isCreatingCfqlTask;

  // Synchronous in-flight guard. `isCreatingCfqlTask` is derived from the
  // mutation's `isLoading` and only flips to true after a re-render, so two
  // click handlers dispatched in the same frame both observe it as false and
  // each fire a mutation. A ref updates synchronously, so the second handler
  // in the same frame sees the lock immediately and bails out (ANOW-6951).
  const isSendingCfqlRef = useRef(false);

  const handleSendCfql2Assessment = useCallback(async () => {
    if (!clientUserId || !clientGuid || !clientId) return;
    // Guard against stacking: never send when one is already pending, and
    // bail while a create is in-flight so a fast double-click can't enqueue a
    // duplicate before the pending-task query refreshes.
    if (hasPendingCfqlTask || isSendingCfqlRef.current) return;

    const normalizedClientId = Number(clientId);
    if (!Number.isFinite(normalizedClientId) || normalizedClientId <= 0) return;

    isSendingCfqlRef.current = true;
    try {
      await createTaskAsync({
        userId: clientUserId,
        taskData: {
          userId: clientUserId,
          type: "fillout_iframe",
          taskName: "CFQL",
          metadata: {
            guid: clientGuid,
            clientId: normalizedClientId,
            administratorUserId: clientUserId,
          },
        },
      });
      setCfql2SendResult("success");
    } catch {
      setCfql2SendResult("error");
    } finally {
      isSendingCfqlRef.current = false;
    }
  }, [clientUserId, clientGuid, clientId, hasPendingCfqlTask, createTaskAsync]);

  const handleSendQabfAssessment = useCallback(
    (goalId: string) => {
      // Get the QABF definition to construct the fillout URL with the behavior reduction goal
      const qabfDefinition = getAssessmentDefinition("qabf");
      const baseUrl = qabfDefinition.filloutUrl;

      if (baseUrl) {
        const params = new URLSearchParams({ goalId });
        if (clientGuid) params.set("guid", clientGuid);
        if (clinicianUserId != null) params.set("administratorUserId", String(clinicianUserId));
        window.open(`${baseUrl}?${params.toString()}`, "_blank", "noopener,noreferrer");
      }
    },
    [clientGuid, clinicianUserId]
  );

  const assessmentsByType = useMemo(() => {
    return getAssessmentRecordsByType(clientAssessments || []);
  }, [clientAssessments]);

  const selectedAssessmentRecords = useMemo<AssessmentRecord[]>(() => {
    if (!clientAssessments?.length) {
      return [];
    }

    return assessmentsByType[selectedAssessmentType] || [];
  }, [assessmentsByType, clientAssessments, selectedAssessmentType]);

  // Most-recent record for the currently-selected type; used to seed form defaults.
  const mostRecentAssessment = useMemo<AssessmentRecord | null>(() => {
    const records = assessmentsByType[selectedAssessmentType] ?? [];
    if (!records.length) return null;
    return records.reduce((latest, rec) => {
      const latestTime = new Date(latest.evaluation_date || latest.created).getTime();
      const recTime = new Date(rec.evaluation_date || rec.created).getTime();
      return recTime > latestTime ? rec : latest;
    });
  }, [assessmentsByType, selectedAssessmentType]);

  if (assessmentsLoading) {
    return <ANLoadingScreen />;
  }

  // Per-design, errors are surfaced per-card rather than as a page-level error.

  return (
    <Stack component="section" position="relative" direction="row" useFlexGap overflow="hidden">
      <Box
        sx={{
          width:
            isOverlayOpen && overlayMode === "results" ? `calc(100% - ${PANEL_WIDTH}px)` : "100%",
          flexGrow: MAIN_CONTENT_FLEX_GROW,
          p: space5,
          overflowX: "auto",
        }}
      >
        <Stack rowGap={space5} sx={{ minWidth: 516 }}>
          {ASSESSMENT_DEFINITIONS.map((definition) => {
            const dataAssessments = assessmentsByType[definition.type] || [];
            const orderedRecords = [...dataAssessments].reverse();

            return (
              <AssessmentCard
                key={definition.type}
                assessmentType={definition.type}
                dataAssessments={orderedRecords}
                cardState={cardStatusByType?.[definition.type]?.state}
                onRefresh={cardStatusByType?.[definition.type]?.refetch}
                onOpenResults={handleOpenResultsPanel}
                onOpenAddAssessment={handleOpenAddAssessmentDialog}
                onOpenSendQabfDialog={handleOpenQabfDialog}
                onSendCfql2Assessment={handleSendCfql2Assessment}
                sendCfqlDisabled={isSendCfqlDisabled}
                filloutUrlOverride={
                  definition.type !== "cfql2" && definition.filloutUrl && clientGuid
                    ? (() => {
                        const params = new URLSearchParams({ guid: clientGuid });
                        if (clinicianUserId != null)
                          params.set("administratorUserId", String(clinicianUserId));
                        return `${definition.filloutUrl}?${params.toString()}`;
                      })()
                    : undefined
                }
              />
            );
          })}
        </Stack>
      </Box>

      <Drawer
        anchor="right"
        variant="temporary"
        open={isOverlayOpen && overlayMode === "results"}
        onClose={handleCloseOverlay}
        PaperProps={{ "data-testid": "assessment-results-panel" }}
        sx={{
          "& .MuiDrawer-paper": previousPaper as object,
        }}
      >
        <AssessmentResults
          onClosePanel={handleCloseOverlay}
          assessmentType={selectedAssessmentType}
          dataAssessments={selectedAssessmentRecords}
          clientId={clientId}
          currentUserId={clinicianUserId}
        />
      </Drawer>

      {/* Data entry dialog – centered on the content area (excluding left nav). */}
      <Dialog
        open={isOverlayOpen && overlayMode === "addAssessment"}
        onClose={handleCloseOverlay}
        fullWidth
        maxWidth="xs"
        scroll="paper"
        sx={{
          // Shift the overlay right by the nav width so the dialog is centered
          // on the content area rather than the full viewport.
          "& .MuiDialog-container": {
            paddingLeft: { xs: 0, md: `${SIDENAV_WIDTH}px` },
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          {getDataEntryDialogTitle(selectedAssessmentType)}
          <IconButton
            onClick={handleCloseOverlay}
            size="small"
            aria-label="Close data entry dialog"
          >
            <X weight="regular" size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DataEntryDialogContent
            clientId={clientId}
            assessmentType={selectedAssessmentType}
            currentUserName={currentUserName}
            clientAgeInYears={clientAgeInYears}
            mostRecentAssessment={mostRecentAssessment}
            onClose={handleCloseOverlay}
          />
        </DialogContent>
      </Dialog>

      {/* QABF Assessment Dialog – behavior reduction goal selector. */}
      <SendQabfAssessmentDialog
        open={isQabfDialogOpen}
        onClose={handleCloseQabfDialog}
        onSendAssessment={handleSendQabfAssessment}
        clientId={clientId}
      />

      {/* CFQL-2 send feedback */}
      <Snackbar
        open={cfql2SendResult !== null}
        autoHideDuration={5000}
        onClose={() => setCfql2SendResult(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={cfql2SendResult === "success" ? "success" : "error"}
          onClose={() => setCfql2SendResult(null)}
        >
          {cfql2SendResult === "success"
            ? "CFQL-2 assessment sent to client."
            : "Failed to send CFQL-2 assessment. Please try again."}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Assessments;
